import React, { useRef, useLayoutEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { automationTriggerDisplayName } from '@frontend/app/containers/Projects/AutomationConfig/constants';
import { AutomationTriggerType } from '@frontend/app/types/globalTypes';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';
import { NodeIcon } from '../utils/nodeIconMapping';
import { NodeToolbarV2 } from './shared/NodeToolbarV2';
import AddNodeButton from './shared/AddNodeButton';

interface TriggerNodeV2Props {
  data: FlowNodeData;
}

const getTriggerDescription = (triggerType?: string) => {
  // Use the standard V1 automation trigger display names for consistency
  if (triggerType && automationTriggerDisplayName[triggerType as AutomationTriggerType]) {
    return automationTriggerDisplayName[triggerType as AutomationTriggerType];
  }
  return 'This event starts the automation';
};

export const TriggerNodeV2: React.FC<TriggerNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    isEditing,
    metadata,
    isSelected,
    onNodeClick,
    onNodeResize,
  } = data;
  const triggerType = metadata?.triggerType || 'PROGRAM_APPLIED';

  // Height measurement for dynamic spacing
  const nodeRef = useRef<HTMLDivElement>(null);

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  // Height measurement effect for dynamic spacing
  useLayoutEffect(() => {
    if (nodeRef.current && onNodeResize) {
      const measureHeight = () => {
        const height = nodeRef.current?.offsetHeight;
        if (height) {
          onNodeResize(nodeId, height);
        }
      };

      // Initial measurement
      measureHeight();

      // Create ResizeObserver for dynamic height changes
      const resizeObserver = new ResizeObserver(measureHeight);
      resizeObserver.observe(nodeRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [nodeId, onNodeResize, triggerType]); // Re-measure when trigger type changes

  return (
    <div ref={nodeRef} className="relative">
      {/* Main node container - Figma style */}
      <div className="flex flex-col items-start justify-center relative w-[342px] transition-all duration-200">
        {/* Node with border */}
        <div className={cn(
          'w-full bg-white border border-grey-2 rounded-2xl transition-all duration-200 pb-4',
          isSelected && 'border-grey-6 border-[1px] shadow-md shadow-black/10'
        )}
        >
          {/* Header Card */}
          <div
            className="w-full bg-white cursor-pointer rounded-t-2xl"
            onClick={handleNodeClick}
          >
            <div className="flex h-12 items-center gap-1 p-3 relative w-full">
              <div className="flex justify-between flex-1 self-stretch grow items-center relative">
                <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                  <NodeIcon node={{ type: 'TRIGGER' }} size="md" />
                  <div className="relative w-fit font-medium text-grey-6 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                    When
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Card */}
          <div className="w-full bg-white rounded-b-2xl overflow-hidden">
            <div className="flex flex-col items-start gap-3 px-3 pb-3 relative w-full bg-white">
              <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-grey-1 rounded-xl overflow-hidden">
                <div className="text-sm font-medium text-grey-6">
                  {getTriggerDescription(triggerType)}
                </div>
                <div className="gap-2 p-1.5 flex w-6 h-6 items-center justify-center relative rounded-lg">
                  <FontAwesomeIcon icon={faLock} className="w-3 h-3 text-grey-4" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom connector with add button - positioned to show half inside border */}
        {isEditing && (
          <AddNodeButton data={data} nodeId={nodeId} />
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-0 h-0 opacity-0"
      />

      {/* Node Toolbar */}
      {data.isToolbarVisible && (
        <NodeToolbarV2
          nodeId={nodeId}
          isVisible
          onAddNode={data.onAddChild}
          onClose={() => {
            data.onCloseToolbar?.();
            data.onShowDashedPlaceholder?.(null);
          }}
          position={Position.Right}
          offset={15}
        />
      )}
    </div>
  );
};
